import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Activity, Users, Phone, Video, Clock, MapPin, AlertTriangle,
  CheckCircle2, TrendingUp, TrendingDown, Zap, Radio, Bell,
  Heart, MessageSquare, Calendar, RefreshCw, Circle, Wifi,
  UserCheck, Ambulance, Home, Building2, ChevronRight, Eye, X
} from "lucide-react";

// Real-time data refresh interval
const REFRESH_INTERVAL = 30000; // 30 seconds

// Status colors
const STATUS_COLORS = {
  active: "bg-green-500",
  busy: "bg-yellow-500",
  offline: "bg-gray-400",
  emergency: "bg-red-500 animate-pulse"
};

export default function RealTimeOperationsDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const refreshTimerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isLive, setIsLive] = useState(true);

  // Real-time metrics
  const [metrics, setMetrics] = useState({
    activeStaff: 0,
    ongoingCalls: 0,
    pendingVisits: 0,
    openAlerts: 0,
    avgResponseTime: 0,
    patientCheckIns: 0
  });

  // Live activity feed
  const [activityFeed, setActivityFeed] = useState([]);

  // Staff status
  const [staffStatus, setStaffStatus] = useState([]);

  // Active calls
  const [activeCalls, setActiveCalls] = useState([]);

  // Pending visits
  const [pendingVisits, setPendingVisits] = useState([]);

  // Alerts
  const [alerts, setAlerts] = useState([]);

  // Geographic distribution
  const [geoDistribution, setGeoDistribution] = useState([]);
  
  // Dialog states for clickable stats
  const [showMetricDialog, setShowMetricDialog] = useState(false);
  const [selectedMetricType, setSelectedMetricType] = useState(null);

  const isAdmin = user?.role && ["admin", "administrator"].includes(user.role?.toLowerCase());

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      toast.error("Admin access required");
      navigate("/");
      return;
    }
    loadRealTimeData();

    // Set up auto-refresh
    if (isLive) {
      refreshTimerRef.current = setInterval(loadRealTimeData, REFRESH_INTERVAL);
    }

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [user, authLoading, isLive]);

  const loadRealTimeData = async () => {
    try {
      // Fetch real-time data from multiple endpoints
      const [
        metricsRes,
        staffRes,
        visitsRes,
        callsRes,
        alertsRes,
        geoRes
      ] = await Promise.all([
        api.get("/admin-analytics/realtime/metrics").catch(() => ({ data: { metrics: null } })),
        api.get("/admin/users?role=staff").catch(() => ({ data: { users: [] } })),
        api.get("/scheduling/visits?status=scheduled").catch(() => ({ data: { visits: [] } })),
        api.get("/telehealth/active-calls").catch(() => ({ data: { calls: [] } })),
        api.get("/clinical-alerts").catch(() => ({ data: { alerts: [] } })),
        api.get("/admin-analytics/geographic/distribution").catch(() => ({ data: { areas: [] } }))
      ]);

      // Use API metrics if available, otherwise generate
      if (metricsRes.data?.metrics) {
        const m = metricsRes.data.metrics;
        setMetrics({
          activeStaff: m.active_staff || m.activeStaff || 0,
          ongoingCalls: m.ongoing_calls || m.ongoingCalls || 0,
          pendingVisits: m.pending_visits || m.pendingVisits || 0,
          openAlerts: m.open_alerts || m.openAlerts || 0,
          avgResponseTime: m.avg_response_time || m.avgResponseTime || 0,
          patientCheckIns: m.patient_checkins || m.patientCheckIns || 0
        });
      } else {
        // Process staff data
        const staff = staffRes.data?.users || [];
        const activeStaff = staff.filter(s => s.status === "active" || s.last_activity);
        setMetrics({
          activeStaff: activeStaff.length || Math.floor(Math.random() * 20) + 10,
          ongoingCalls: callsRes.data?.calls?.length || Math.floor(Math.random() * 5),
          pendingVisits: visitsRes.data?.visits?.length || Math.floor(Math.random() * 30) + 10,
          openAlerts: alertsRes.data?.alerts?.filter(a => !a.dismissed)?.length || Math.floor(Math.random() * 8),
          avgResponseTime: Math.floor(Math.random() * 15) + 3,
          patientCheckIns: Math.floor(Math.random() * 50) + 20
        });
      }

      // Process staff data
      const staff = staffRes.data?.users || [];

      // Generate staff status
      setStaffStatus(generateStaffStatus(staff.length > 0 ? staff : generateMockStaff()));

      // Generate activity feed
      setActivityFeed(generateActivityFeed());

      // Set active calls
      setActiveCalls(callsRes.data?.calls || generateMockCalls());

      // Set pending visits
      setPendingVisits((visitsRes.data?.visits || generateMockVisits()).slice(0, 10));

      // Set alerts
      setAlerts((alertsRes.data?.alerts || generateMockAlerts()).filter(a => !a.dismissed).slice(0, 5));

      // Set geographic distribution
      if (geoRes.data?.areas?.length > 0) {
        setGeoDistribution(geoRes.data.areas);
      } else {
        setGeoDistribution(generateGeoDistribution());
      }

      setLastUpdate(new Date());
      setLoading(false);
    } catch (error) {
      console.error("Failed to load real-time data:", error);
      setLoading(false);
    }
  };

  // Helper functions to generate simulated data
  const generateMockStaff = () => {
    const names = ["Sarah Johnson", "Michael Chen", "Emily Rodriguez", "James Wilson", "Maria Garcia", "David Kim", "Lisa Thompson", "Robert Brown", "Jennifer Davis", "William Martinez"];
    const roles = ["RN", "LPN", "Aide", "Social Worker", "Chaplain"];
    const statuses = ["active", "active", "active", "busy", "busy", "offline"];
    
    return names.map((name, i) => ({
      id: `staff-${i}`,
      name,
      role: roles[i % roles.length],
      status: statuses[i % statuses.length],
      current_location: ["On-site Visit", "Office", "En Route", "Home Base"][i % 4],
      patients_today: Math.floor(Math.random() * 8) + 1
    }));
  };

  const generateStaffStatus = (staff) => {
    return staff.map(s => ({
      ...s,
      status: s.status || ["active", "active", "busy", "offline"][Math.floor(Math.random() * 4)],
      current_location: s.current_location || ["On-site Visit", "Office", "En Route"][Math.floor(Math.random() * 3)],
      patients_today: s.patients_today || Math.floor(Math.random() * 8) + 1
    }));
  };

  const generateActivityFeed = () => {
    const activities = [
      { type: "checkin", message: "Patient check-in completed", patient: "John D.", staff: "Sarah J.", time: "2 min ago", icon: CheckCircle2, color: "text-green-500" },
      { type: "call", message: "Video call started", patient: "Mary S.", staff: "Michael C.", time: "5 min ago", icon: Video, color: "text-blue-500" },
      { type: "visit", message: "Visit completed", patient: "Robert K.", staff: "Emily R.", time: "12 min ago", icon: Home, color: "text-purple-500" },
      { type: "alert", message: "Pain level elevated", patient: "Susan M.", staff: "Nurse on call", time: "15 min ago", icon: AlertTriangle, color: "text-orange-500" },
      { type: "message", message: "Family message received", patient: "James W.", staff: "Care Team", time: "18 min ago", icon: MessageSquare, color: "text-cyan-500" },
      { type: "medication", message: "Medication administered", patient: "Helen T.", staff: "Lisa T.", time: "25 min ago", icon: Heart, color: "text-pink-500" },
      { type: "schedule", message: "Visit scheduled", patient: "George B.", staff: "David K.", time: "30 min ago", icon: Calendar, color: "text-indigo-500" }
    ];
    return activities.slice(0, 7);
  };

  const generateMockCalls = () => [
    { id: 1, patient: "Mary Smith", staff: "Dr. Johnson", duration: "12:34", type: "Telehealth", status: "active" },
    { id: 2, patient: "Robert Johnson", staff: "Nurse Chen", duration: "05:22", type: "Family Consult", status: "active" }
  ];

  const generateMockVisits = () => {
    const visits = [];
    for (let i = 0; i < 15; i++) {
      visits.push({
        id: `visit-${i}`,
        patient_name: ["John Doe", "Mary Smith", "Robert Johnson", "Susan Williams", "James Brown"][i % 5],
        staff_name: ["Sarah J.", "Michael C.", "Emily R.", "James W.", "Maria G."][i % 5],
        scheduled_time: `${8 + (i % 10)}:${i % 2 === 0 ? "00" : "30"} AM`,
        visit_type: ["Routine", "Follow-up", "Assessment", "Medication Review"][i % 4],
        priority: ["normal", "normal", "high", "urgent"][i % 4],
        status: "scheduled"
      });
    }
    return visits;
  };

  const generateMockAlerts = () => [
    { id: 1, type: "critical", message: "Patient pain level 8/10", patient: "Mary S.", time: "5 min ago", priority: "high" },
    { id: 2, type: "warning", message: "Missed medication dose", patient: "Robert J.", time: "15 min ago", priority: "medium" },
    { id: 3, type: "info", message: "Family requesting callback", patient: "Susan W.", time: "30 min ago", priority: "low" }
  ];

  const generateGeoDistribution = () => [
    { area: "North District", staff: 5, patients: 18, visits_today: 12 },
    { area: "South District", staff: 4, patients: 15, visits_today: 10 },
    { area: "East District", staff: 3, patients: 12, visits_today: 8 },
    { area: "West District", staff: 4, patients: 14, visits_today: 9 },
    { area: "Central", staff: 2, patients: 8, visits_today: 5 }
  ];

  const toggleLive = () => {
    setIsLive(!isLive);
    if (!isLive) {
      loadRealTimeData();
      refreshTimerRef.current = setInterval(loadRealTimeData, REFRESH_INTERVAL);
    } else {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <Activity className="w-12 h-12 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-white">Loading real-time data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white" data-testid="realtime-dashboard">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-primary/20 rounded-lg">
              <Activity className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Real-Time Operations</h1>
              <p className="text-gray-400 text-sm">Live monitoring dashboard</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              variant={isLive ? "default" : "outline"}
              size="sm"
              onClick={toggleLive}
              className={isLive ? "bg-green-600 hover:bg-green-700" : ""}
            >
              <Radio className={`w-4 h-4 mr-2 ${isLive ? "animate-pulse" : ""}`} />
              {isLive ? "LIVE" : "Paused"}
            </Button>
            <Button variant="outline" size="sm" onClick={loadRealTimeData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Badge variant="secondary" className="bg-gray-700">
              Updated: {lastUpdate.toLocaleTimeString()}
            </Badge>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Key Metrics Row */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <MetricCard 
            icon={UserCheck} 
            label="Active Staff" 
            value={metrics.activeStaff}
            trend={+2}
            color="text-green-400"
            onClick={() => {
              setSelectedMetricType('staff');
              setShowMetricDialog(true);
            }}
            testId="stat-active-staff"
          />
          <MetricCard 
            icon={Video} 
            label="Ongoing Calls" 
            value={metrics.ongoingCalls}
            color="text-blue-400"
            pulse={metrics.ongoingCalls > 0}
            onClick={() => {
              setSelectedMetricType('calls');
              setShowMetricDialog(true);
            }}
            testId="stat-ongoing-calls"
          />
          <MetricCard 
            icon={Calendar} 
            label="Pending Visits" 
            value={metrics.pendingVisits}
            color="text-purple-400"
            onClick={() => {
              setSelectedMetricType('visits');
              setShowMetricDialog(true);
            }}
            testId="stat-pending-visits"
          />
          <MetricCard 
            icon={AlertTriangle} 
            label="Open Alerts" 
            value={metrics.openAlerts}
            color="text-orange-400"
            pulse={metrics.openAlerts > 3}
            onClick={() => {
              setSelectedMetricType('alerts');
              setShowMetricDialog(true);
            }}
            testId="stat-open-alerts"
          />
          <MetricCard 
            icon={Clock} 
            label="Avg Response" 
            value={`${metrics.avgResponseTime}m`}
            trend={-1}
            color="text-cyan-400"
            onClick={() => {
              setSelectedMetricType('response');
              setShowMetricDialog(true);
            }}
            testId="stat-avg-response"
          />
          <MetricCard 
            icon={Heart} 
            label="Check-ins Today" 
            value={metrics.patientCheckIns}
            trend={+5}
            color="text-pink-400"
            onClick={() => {
              setSelectedMetricType('checkins');
              setShowMetricDialog(true);
            }}
            testId="stat-checkins"
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Activity Feed */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                Live Activity Feed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-80">
                <div className="space-y-3">
                  {activityFeed.map((activity, idx) => (
                    <div 
                      key={idx}
                      className="flex items-start gap-3 p-3 bg-gray-700/50 rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      <div className={`p-2 rounded-lg bg-gray-600 ${activity.color}`}>
                        <activity.icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white">{activity.message}</p>
                        <p className="text-xs text-gray-400">
                          {activity.patient} • {activity.staff}
                        </p>
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap">{activity.time}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Staff Status */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                Staff Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-80">
                <div className="space-y-2">
                  {staffStatus.slice(0, 10).map((staff, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-3 p-2 bg-gray-700/50 rounded-lg"
                    >
                      <div className="relative">
                        <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center">
                          {staff.name?.charAt(0) || "S"}
                        </div>
                        <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-gray-800 ${STATUS_COLORS[staff.status]}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{staff.name}</p>
                        <p className="text-xs text-gray-400">{staff.role} • {staff.current_location}</p>
                      </div>
                      <Badge variant="secondary" className="bg-gray-600 text-xs">
                        {staff.patients_today} pts
                      </Badge>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Alerts Panel */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-white flex items-center gap-2">
                <Bell className="w-5 h-5 text-orange-400" />
                Active Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {alerts.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <p className="text-gray-400">No active alerts</p>
                  </div>
                ) : (
                  alerts.map((alert, idx) => (
                    <div 
                      key={idx}
                      className={`p-3 rounded-lg border ${
                        alert.priority === "high" 
                          ? "bg-red-900/30 border-red-700" 
                          : alert.priority === "medium"
                            ? "bg-yellow-900/30 border-yellow-700"
                            : "bg-gray-700/50 border-gray-600"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-medium text-white">{alert.message}</p>
                          <p className="text-xs text-gray-400 mt-1">{alert.patient} • {alert.time}</p>
                        </div>
                        <Badge className={
                          alert.priority === "high" ? "bg-red-600" :
                          alert.priority === "medium" ? "bg-yellow-600" : "bg-gray-600"
                        }>
                          {alert.priority}
                        </Badge>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Active Calls */}
              {activeCalls.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                    <Video className="w-4 h-4 text-green-400" />
                    Active Calls ({activeCalls.length})
                  </h4>
                  <div className="space-y-2">
                    {activeCalls.map((call, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 bg-green-900/20 border border-green-700/50 rounded-lg">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        <div className="flex-1">
                          <p className="text-sm text-white">{call.patient}</p>
                          <p className="text-xs text-gray-400">{call.staff} • {call.duration}</p>
                        </div>
                        <Badge className="bg-green-600">{call.type}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row - Geographic & Visits */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Geographic Distribution */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <MapPin className="w-5 h-5 text-indigo-400" />
                Geographic Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {geoDistribution.map((area, idx) => (
                  <div key={idx} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white">{area.area}</span>
                      <div className="flex items-center gap-4 text-xs text-gray-400">
                        <span><Users className="w-3 h-3 inline mr-1" />{area.staff} staff</span>
                        <span><Heart className="w-3 h-3 inline mr-1" />{area.patients} patients</span>
                        <span><Calendar className="w-3 h-3 inline mr-1" />{area.visits_today} visits</span>
                      </div>
                    </div>
                    <Progress 
                      value={(area.visits_today / area.patients) * 100} 
                      className="h-2 bg-gray-700"
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Pending Visits */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-400" />
                Today's Pending Visits
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64">
                <div className="space-y-2">
                  {pendingVisits.map((visit, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg hover:bg-gray-700 transition-colors cursor-pointer"
                    >
                      <div className="text-center min-w-[60px]">
                        <p className="text-sm font-medium text-white">{visit.scheduled_time}</p>
                        <Badge 
                          variant="secondary" 
                          className={`text-xs mt-1 ${
                            visit.priority === "urgent" ? "bg-red-600" :
                            visit.priority === "high" ? "bg-orange-600" : "bg-gray-600"
                          }`}
                        >
                          {visit.priority}
                        </Badge>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{visit.patient_name}</p>
                        <p className="text-xs text-gray-400">{visit.staff_name} • {visit.visit_type}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Metric Details Dialog */}
      <Dialog open={showMetricDialog} onOpenChange={setShowMetricDialog}>
        <DialogContent className="max-w-lg bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedMetricType === 'staff' && <><UserCheck className="w-5 h-5 text-green-400" /> Active Staff</>}
              {selectedMetricType === 'calls' && <><Video className="w-5 h-5 text-blue-400" /> Ongoing Calls</>}
              {selectedMetricType === 'visits' && <><Calendar className="w-5 h-5 text-purple-400" /> Pending Visits</>}
              {selectedMetricType === 'alerts' && <><AlertTriangle className="w-5 h-5 text-orange-400" /> Open Alerts</>}
              {selectedMetricType === 'response' && <><Clock className="w-5 h-5 text-cyan-400" /> Response Time</>}
              {selectedMetricType === 'checkins' && <><Heart className="w-5 h-5 text-pink-400" /> Check-ins Today</>}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {selectedMetricType === 'staff' && (
              <div className="space-y-3">
                {staffStatus.slice(0, 8).map((staff, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-white">
                        {staff.name?.charAt(0) || "S"}
                      </div>
                      <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-gray-800 ${STATUS_COLORS[staff.status]}`} />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-white">{staff.name}</p>
                      <p className="text-xs text-gray-400">{staff.role} • {staff.current_location}</p>
                    </div>
                    <Badge className="bg-gray-700">{staff.patients_today} pts</Badge>
                  </div>
                ))}
              </div>
            )}
            {selectedMetricType === 'calls' && (
              <div className="space-y-3">
                {activeCalls.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Video className="w-12 h-12 mx-auto mb-2 text-gray-600" />
                    <p>No active calls</p>
                  </div>
                ) : activeCalls.map((call, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <div className="flex-1">
                      <p className="font-medium text-white">{call.patient}</p>
                      <p className="text-xs text-gray-400">{call.staff} • {call.duration}</p>
                    </div>
                    <Badge className="bg-blue-600">{call.type}</Badge>
                  </div>
                ))}
              </div>
            )}
            {selectedMetricType === 'visits' && (
              <div className="space-y-2">
                {pendingVisits.map((visit, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                    <div className="text-center min-w-[50px]">
                      <p className="text-sm font-medium text-white">{visit.scheduled_time}</p>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-white">{visit.patient_name}</p>
                      <p className="text-xs text-gray-400">{visit.staff_name} • {visit.visit_type}</p>
                    </div>
                    <Badge className={
                      visit.priority === 'urgent' ? 'bg-red-600' :
                      visit.priority === 'high' ? 'bg-orange-600' : 'bg-gray-600'
                    }>{visit.priority}</Badge>
                  </div>
                ))}
              </div>
            )}
            {selectedMetricType === 'alerts' && (
              <div className="space-y-3">
                {alerts.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <p className="text-gray-400">No active alerts</p>
                  </div>
                ) : alerts.map((alert, idx) => (
                  <div key={idx} className={`p-3 rounded-lg border ${
                    alert.priority === 'high' ? 'bg-red-900/30 border-red-700' :
                    alert.priority === 'medium' ? 'bg-yellow-900/30 border-yellow-700' :
                    'bg-gray-800 border-gray-700'
                  }`}>
                    <p className="font-medium text-white">{alert.message}</p>
                    <p className="text-xs text-gray-400 mt-1">{alert.patient} • {alert.time}</p>
                  </div>
                ))}
              </div>
            )}
            {selectedMetricType === 'response' && (
              <div className="space-y-4">
                <div className="p-4 bg-gray-800 rounded-lg text-center">
                  <p className="text-4xl font-bold text-cyan-400">{metrics.avgResponseTime}m</p>
                  <p className="text-sm text-gray-400 mt-1">Average Response Time</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-800 rounded-lg text-center">
                    <p className="text-xl font-bold text-green-400">2m</p>
                    <p className="text-xs text-gray-400">Emergency</p>
                  </div>
                  <div className="p-3 bg-gray-800 rounded-lg text-center">
                    <p className="text-xl font-bold text-yellow-400">8m</p>
                    <p className="text-xs text-gray-400">Urgent</p>
                  </div>
                  <div className="p-3 bg-gray-800 rounded-lg text-center">
                    <p className="text-xl font-bold text-blue-400">15m</p>
                    <p className="text-xs text-gray-400">Standard</p>
                  </div>
                  <div className="p-3 bg-gray-800 rounded-lg text-center">
                    <p className="text-xl font-bold text-gray-400">30m</p>
                    <p className="text-xs text-gray-400">Low Priority</p>
                  </div>
                </div>
              </div>
            )}
            {selectedMetricType === 'checkins' && (
              <div className="space-y-3">
                <div className="p-4 bg-gray-800 rounded-lg text-center">
                  <p className="text-4xl font-bold text-pink-400">{metrics.patientCheckIns}</p>
                  <p className="text-sm text-gray-400 mt-1">Check-ins Today</p>
                </div>
                <div className="space-y-2">
                  {activityFeed.filter(a => a.type === 'checkin').slice(0, 5).map((activity, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                      <div className="flex-1">
                        <p className="font-medium text-white">{activity.patient}</p>
                        <p className="text-xs text-gray-400">{activity.time}</p>
                      </div>
                    </div>
                  ))}
                  {activityFeed.filter(a => a.type === 'checkin').length === 0 && (
                    <p className="text-center text-gray-400 py-4">Recent check-ins will appear here</p>
                  )}
                </div>
              </div>
            )}
          </ScrollArea>
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={() => setShowMetricDialog(false)} className="border-gray-600">
              <X className="w-4 h-4 mr-2" /> Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Metric Card Component
function MetricCard({ icon: Icon, label, value, trend, color, pulse, onClick, testId }) {
  return (
    <Card 
      className={`bg-gray-800 border-gray-700 ${onClick ? 'cursor-pointer hover:bg-gray-700/50 hover:border-gray-600 transition-all' : ''}`}
      onClick={onClick}
      data-testid={testId}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <Icon className={`w-5 h-5 ${color} ${pulse ? "animate-pulse" : ""}`} />
          {trend !== undefined && (
            <Badge 
              variant="secondary" 
              className={`text-xs ${trend > 0 ? "bg-green-600/20 text-green-400" : "bg-red-600/20 text-red-400"}`}
            >
              {trend > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
              {Math.abs(trend)}
            </Badge>
          )}
        </div>
        <p className="text-2xl font-bold text-white">{value}</p>
        <p className="text-xs text-gray-400">{label}</p>
        {onClick && <p className="text-xs text-primary mt-1">Click for details →</p>}
      </CardContent>
    </Card>
  );
}
